import { BillFilters } from "@common/contracts/bill.contract";
import { toBillFilters } from "@common/helpers/domains/bills/billTaskFilters";
import { omit } from "@common/helpers/objects";
import { FiltersTypes } from "@common/types/filters/general";
import {
    ActiveFilter,
    ActiveFilterToFilterTabsMap,
    activeFiltersOrder,
    FilterTabs,
    isActive,
} from "@components/pageHeader/filters/lib";
import { BILLS_TASK_SCOPE_KEYS } from "./billsTaskView";

/**
 * Filters that need real workspace data (provider accounts, the vendor list)
 * are offered only once a task is scoped to a workspace. Org-level (non client
 * specific) templates keep to the workspace-agnostic subset and use the
 * vendor presence toggle instead of the vendor picker.
 */
const ORG_LEVEL_FILTER_TABS: FilterTabs[] = [
    FilterTabs.BILL_STATUS_FILTER,
    FilterTabs.DUE,
    FilterTabs.AMOUNT,
    FilterTabs.MATCH_STATUS,
    FilterTabs.TEMPLATE_COUNTERPARTY,
];

const WORKSPACE_FILTER_TABS: FilterTabs[] = [
    FilterTabs.BILL_STATUS_FILTER,
    FilterTabs.DUE,
    FilterTabs.AMOUNT,
    FilterTabs.ACCOUNT,
    FilterTabs.MATCH_STATUS,
    FilterTabs.COUNTERPARTY,
];

interface GetBillsTaskFilterTabsParams {
    isWorkspaceScoped: boolean;
}

export const getBillsTaskFilterTabs = ({
    isWorkspaceScoped,
}: GetBillsTaskFilterTabsParams): FilterTabs[] =>
    isWorkspaceScoped ? WORKSPACE_FILTER_TABS : ORG_LEVEL_FILTER_TABS;

const LOCKABLE_FILTER_TABS = new Set<FilterTabs>(WORKSPACE_FILTER_TABS);

/**
 * Which `originalFilters` keys of a sticky task render as locked pills: the
 * creator-set matching criteria that built the bill snapshot.
 */
export function getBillsLockedActiveFilters(
    originalFilters: FiltersTypes | undefined,
): ActiveFilter[] {
    if (!originalFilters) {
        return [];
    }
    return activeFiltersOrder.filter(
        (filterId) =>
            LOCKABLE_FILTER_TABS.has(ActiveFilterToFilterTabsMap[filterId]) &&
            isActive[filterId](originalFilters),
    );
}

/** Task scope keys live on the view but are not shown as filter pills. */
export function toBillsOverlayFilters(filters: FiltersTypes): FiltersTypes {
    return omit(filters, [...BILLS_TASK_SCOPE_KEYS, "billIds"]);
}

interface BuildBillsTaskQueryParams {
    /** Live view filters (user filter edits, pinned ids for static tasks). */
    viewFilters: FiltersTypes;
    /** Creator-set criteria of a sticky task. Present only for sticky tasks. */
    originalFilters?: FiltersTypes;
    /** Sticky membership snapshot. Present only for sticky tasks. */
    stickyBillIds?: string[];
}

/**
 * Query behind a bills task table, shared by the task body and the creation
 * preview. A sticky task reads exactly its pinned membership (`null` while the
 * snapshot is empty, which must not fall back to every bill); all other tasks
 * apply the view filters directly.
 */
export function buildBillsTaskQuery({
    viewFilters,
    originalFilters,
    stickyBillIds,
}: BuildBillsTaskQueryParams): BillFilters | null {
    if (originalFilters === undefined) {
        return toBillFilters(viewFilters);
    }
    if (!stickyBillIds?.length) {
        return null;
    }
    return { ids: stickyBillIds, search: viewFilters.search };
}
